/**
 * Hebrew Text Animation Module
 *
 * Renders "שבת שלום" (Shabbat Shalom) above the scroll.
 * Features:
 * - Letters fade in sequentially (right to left, Hebrew style)
 * - Subtle float/breathing animation
 * - Uses system Hebrew fonts with fallback
 */

import { LOADER_COLORS, HEBREW_FONTS, type AnimationModule, type AnimationPhase } from '../types';
import { Easing, lerp, clamp } from './AnimationEngine';

interface Letter {
  char: string;
  x: number;
  targetX: number;
  opacity: number;
  targetOpacity: number;
  y: number;
  baseY: number;
  scale: number;
  delay: number; // Stagger delay for sequential fade-in
}

export class HebrewTextAnimation implements AnimationModule {
  id = 'hebrew-text';

  private ctx: CanvasRenderingContext2D | null = null;
  private width = 0;

  // Text configuration
  private readonly hebrewText = 'שבת שלום';
  private letters: Letter[] = [];
  private fontSize = 32;
  private textY = 0;

  // Animation timing
  private time = 0;

  // Breathing animation parameters
  private breathePhase = 0;
  private breatheAmplitude = 3;

  init(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    this.ctx = ctx;
    this.resize(width, height);
  }

  resize(width: number, height: number): void {
    this.width = width;

    // Calculate text size and position
    // Increased 25% for better mobile visibility
    const minDimension = Math.min(width, height);
    const scale = 0.75; // 25% larger than previous 0.6
    this.fontSize = minDimension * 0.06 * scale;

    // Position above the Torah scroll - adjusted for smaller scroll
    const centerY = height / 2 + 5;
    const scrollHeight = minDimension * 0.35 * scale;
    this.textY = centerY - scrollHeight * 0.85;

    // Set up letter positions
    this.setupLetters();
  }

  private setupLetters(): void {
    if (!this.ctx) return;

    const ctx = this.ctx;
    ctx.font = `${this.fontSize}px ${HEBREW_FONTS.primary}`;

    // Measure total text width
    const totalWidth = ctx.measureText(this.hebrewText).width;

    // Split into individual characters (including space)
    const chars = this.hebrewText.split('');

    // Calculate positions - Hebrew reads right to left
    let currentX = this.width / 2 + totalWidth / 2;

    this.letters = chars.map((char, index) => {
      const charWidth = ctx.measureText(char).width;
      currentX -= charWidth;

      // Stagger delay - rightmost letters appear first (Hebrew reading order)
      const delay = index * 0.08;

      return {
        char,
        x: currentX + charWidth / 2,
        targetX: currentX + charWidth / 2,
        opacity: 0,
        targetOpacity: 1,
        y: this.textY,
        baseY: this.textY,
        scale: 1,
        delay,
      };
    });
  }

  update(deltaTime: number, phase: AnimationPhase, progress: number): void {
    const dt = deltaTime / 1000;
    this.time += dt;
    this.breathePhase += dt * 2;

    switch (phase) {
      case 'intro':
        this.updateIntro(progress);
        break;

      case 'loop':
        this.updateLoop();
        break;

      case 'exit':
        this.updateExit(progress);
        break;

      case 'complete':
        this.letters.forEach((letter) => {
          letter.opacity = 0;
        });
        break;
    }

    // Smooth interpolation for all letters
    this.letters.forEach((letter) => {
      letter.opacity = lerp(letter.opacity, letter.targetOpacity, dt * 8);
      letter.x = lerp(letter.x, letter.targetX, dt * 5);
    });
  }

  private updateIntro(progress: number): void {
    // All letters fade in together for cleaner display
    this.letters.forEach((letter) => {
      // Simple fade in - all letters together, starting at 20% progress
      const adjustedProgress = clamp((progress - 0.1) * 1.5, 0, 1);
      letter.targetOpacity = Easing.easeOutCubic(adjustedProgress);

      // Gentle float down from above
      const introOffset = (1 - Easing.easeOutQuad(adjustedProgress)) * 10;
      letter.y = letter.baseY - introOffset;
    });
  }

  private updateLoop(): void {
    // All letters visible with gentle breathing
    this.letters.forEach((letter, index) => {
      letter.targetOpacity = 1;

      // Staggered breathing animation
      const breatheOffset = Math.sin(this.breathePhase + index * 0.3) * this.breatheAmplitude;
      letter.y = letter.baseY + breatheOffset;
    });
  }

  private updateExit(progress: number): void {
    // Letters fade out and float upward
    this.letters.forEach((letter, index) => {
      // Reverse stagger - leftmost letters exit first
      const reverseDelay = (this.letters.length - 1 - index) * 0.05;
      const adjustedProgress = clamp((progress - reverseDelay) * 2, 0, 1);

      letter.targetOpacity = 1 - Easing.easeInCubic(adjustedProgress);

      // Float upward during exit
      const exitOffset = Easing.easeInQuad(adjustedProgress) * 50;
      letter.y = letter.baseY - exitOffset;
    });
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    // Set up font
    ctx.font = `${this.fontSize}px ${HEBREW_FONTS.primary}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Render each letter
    this.letters.forEach((letter) => {
      if (letter.opacity <= 0.01) return;

      ctx.save();
      ctx.globalAlpha = letter.opacity;

      // Letter position
      ctx.translate(letter.x, letter.y);
      ctx.scale(letter.scale, letter.scale);

      // Draw text shadow for depth
      ctx.fillStyle = 'rgba(99, 52, 94, 0.3)';
      ctx.fillText(letter.char, 1, 1);

      // Draw main text
      ctx.fillStyle = LOADER_COLORS.primaryDeep;
      ctx.fillText(letter.char, 0, 0);

      ctx.restore();
    });

    ctx.restore();
  }

  triggerExit(): void {
    // Exit animation is handled through phase updates
  }

  destroy(): void {
    this.ctx = null;
    this.letters = [];
  }
}
